import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Lock, ArrowRight, Eye, EyeOff, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import SEO from '@/components/SEO';

const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const translations = {
    tr: {
      title: 'Yeni Şifre Belirle',
      subtitle: 'Hesabınız için yeni bir şifre oluşturun',
      password: 'Yeni Şifre',
      passwordPlaceholder: 'Min. 8 karakter',
      confirmPassword: 'Şifre Tekrar',
      resetPassword: 'Şifreyi Güncelle',
      resetting: 'Güncelleniyor...',
      backToLogin: 'Giriş Sayfasına Dön',
      passwordMismatch: 'Şifreler eşleşmiyor',
      passwordWeak: 'Şifre en az 8 karakter, büyük/küçük harf, rakam ve özel karakter içermeli',
      invalidToken: 'Geçersiz veya süresi dolmuş link. Lütfen yeni bir şifre sıfırlama isteği gönderin.',
      successTitle: 'Şifre Güncellendi',
      successMessage: 'Şifreniz başarıyla güncellendi. Artık yeni şifrenizle giriş yapabilirsiniz.',
    },
    en: {
      title: 'Set New Password',
      subtitle: 'Create a new password for your account',
      password: 'New Password',
      passwordPlaceholder: 'Min. 8 characters',
      confirmPassword: 'Confirm Password',
      resetPassword: 'Update Password',
      resetting: 'Updating...',
      backToLogin: 'Back to Login',
      passwordMismatch: 'Passwords do not match',
      passwordWeak: 'Password must be at least 8 characters with uppercase, lowercase, number and special character',
      invalidToken: 'Invalid or expired link. Please request a new password reset.',
      successTitle: 'Password Updated',
      successMessage: 'Your password has been successfully updated. You can now login with your new password.',
    }
  };

  const txt = translations[language] || translations.en;

  const validatePassword = (password) => {
    if (password.length < 8) return false;
    if (!/[a-z]/.test(password)) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/\d/.test(password)) return false;
    if (!/[^A-Za-z0-9]/.test(password)) return false;
    return true;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError(txt.invalidToken);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(txt.passwordMismatch);
      return;
    }

    if (!validatePassword(formData.password)) {
      setError(txt.passwordWeak);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const data = await resetPassword(token, formData.password);
      if (data.status === 'success') {
        setIsSuccess(true);
      } else {
        setError(data.message || txt.invalidToken);
      }
    } catch (err) {
      setError(err.message || txt.invalidToken);
    } finally {
      setIsLoading(false);
    }
  };

  // If no token in URL
  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-password-page">
        <SEO page="resetPassword" />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5"></div>
        
        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-lg blur opacity-75"></div>
                <div className="relative bg-gradient-to-r from-emerald-600 to-cyan-600 p-3 rounded-lg">
                  <Shield className="h-8 w-8 text-white" />
                </div>
              </div>
              <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent">
                Threat.live
              </span>
            </Link>
          </div>

          <Card className="border-0 bg-gradient-to-br from-card to-muted/30 shadow-2xl">
            <CardContent className="pt-6">
              <Alert variant="destructive" className="bg-red-500/10 border-red-500/30">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{txt.invalidToken}</AlertDescription>
              </Alert>
              <Link to="/forgot-password" className="block mt-4">
                <Button className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600">
                  {language === 'tr' ? 'Yeni İstek Gönder' : 'Request New Link'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden" data-testid="reset-password-page">
      <SEO page="resetPassword" />
      
      {/* Background Effects */}
      <div className="absolute inset-0 grid-bg mask-radial-fade opacity-50 pointer-events-none"></div>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="relative sharp p-3 bg-primary">
              <Shield className="h-7 w-7 text-primary-foreground" />
            </div>
            <span className="text-2xl font-bold tracking-tight">
              Threat<span className="text-primary">.</span>live
            </span>
          </Link>
        </div>

        <Card className="sharp border-border bg-card/50 backdrop-blur shadow-xl">
          <CardHeader className="text-center pb-2 border-b border-border bg-muted/30">
            <div className="soc-badge mx-auto mb-3">
              <span className="status-dot" />
              <span>// PASSWORD RESET</span>
            </div>
            <CardTitle className="text-xl font-mono uppercase tracking-wider">{txt.title}</CardTitle>
            <CardDescription className="text-xs">{txt.subtitle}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {isSuccess ? (
              /* Success State */
              <div className="text-center py-6">
                <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{txt.successTitle}</h3>
                <p className="text-muted-foreground text-sm mb-6">
                  {txt.successMessage}
                </p>
                <Link to="/login">
                  <Button className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700">
                    {txt.backToLogin}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ) : (
              /* Form State */
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-500/10 border-red-500/30">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">{txt.password}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={txt.passwordPlaceholder}
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 pr-10"
                      required
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{txt.confirmPassword}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="reset-confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700"
                  disabled={isLoading}
                  data-testid="reset-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {txt.resetting}
                    </>
                  ) : (
                    <>
                      {txt.resetPassword}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Back to Home */}
        <p className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            ← {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
